import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Machine, CreateMachineRequest } from '../../types/machine';
import { apiClient } from '../../services/api';

interface MachinesState {
  machines: Machine[];
  currentMachine: Machine | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  error: string | null;
  searchQuery: string;
  filters: {
    location?: string;
    status?: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const transformMachineResponse = (machine: any): Machine => {
  if (!machine) {
    throw new Error('Machine payload missing');
  }

  return {
    id: machine.id,
    name: machine.name ?? '',
    description: machine.description ?? '',
    imageUrl: machine.image_url ?? machine.imageUrl,
    thumbnailUrl: machine.thumbnail_url ?? machine.thumbnailUrl,
    location: machine.location ?? '',
    status: machine.status ?? 'active',
    createdAt: machine.created_at ?? machine.createdAt ?? '',
    updatedAt: machine.updated_at ?? machine.updatedAt ?? '',
    manuals: machine.manuals,
    spareParts: machine.spare_parts ?? machine.spareParts,
    operatingSupplies: machine.operating_supplies ?? machine.operatingSupplies,
    tools: machine.tools,
  };
};

const initialState: MachinesState = {
  machines: [],
  currentMachine: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  searchQuery: '',
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
};

export const fetchMachines = createAsyncThunk(
  'machines/fetchMachines',
  async (params: {
    page?: number;
    limit?: number;
    search?: string;
    location?: string;
    status?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.location) queryParams.append('location', params.location);
      if (params.status) queryParams.append('status', params.status);

      const response = await apiClient.get(`/machines?${queryParams}`);
      console.log('DEBUG: Raw machines list API response:', response.data);
      if (response.data.machines && response.data.machines.length > 0) {
        console.log('DEBUG: First machine created_at:', response.data.machines[0].created_at, typeof response.data.machines[0].created_at);
      }
      
      // Transform machines from snake_case to camelCase
      if (Array.isArray(response.data.machines)) {
        response.data.machines = response.data.machines.map(transformMachineResponse);
      }

      if (Array.isArray(response.data.data)) {
        response.data.data = response.data.data.map(transformMachineResponse);
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch machines');
    }
  }
);

export const fetchMachine = createAsyncThunk(
  'machines/fetchMachine',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/machines/${id}?include_relations=true`);
      console.log('DEBUG: Raw machine API response:', response.data);
      console.log('DEBUG: Machine created_at:', response.data.created_at, typeof response.data.created_at);
      console.log('DEBUG: Machine updated_at:', response.data.updated_at, typeof response.data.updated_at);

      const transformedMachine = transformMachineResponse(response.data);
      console.log('DEBUG: Transformed machine:', transformedMachine);
      return transformedMachine;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch machine');
    }
  }
);

export const createMachine = createAsyncThunk(
  'machines/createMachine',
  async (machineData: CreateMachineRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/machines', machineData);
      const rawMachine = response.data?.machine ?? response.data?.data ?? response.data;

      if (!rawMachine || !rawMachine.id) {
        const machineId = response.data?.machine?.id ?? response.data?.id;
        if (machineId) {
          const refetch = await apiClient.get(`/machines/${machineId}?include_relations=true`);
          return transformMachineResponse(refetch.data);
        }
        throw new Error('Created machine payload missing identifier');
      }

      return transformMachineResponse(rawMachine);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create machine');
    }
  }
);

export const updateMachine = createAsyncThunk(
  'machines/updateMachine',
  async (params: { id: string; data: Partial<Machine> }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/machines/${params.id}`, params.data);
      let rawMachine = response.data?.machine ?? response.data?.data ?? response.data;

      if (!rawMachine || !rawMachine.id) {
        const refetch = await apiClient.get(`/machines/${params.id}?include_relations=true`);
        rawMachine = refetch.data;
      }

      return transformMachineResponse(rawMachine);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update machine');
    }
  }
);

export const deleteMachine = createAsyncThunk(
  'machines/deleteMachine',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/machines/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete machine');
    }
  }
);

export const uploadMachineImage = createAsyncThunk(
  'machines/uploadMachineImage',
  async (params: { id: string; file: File }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('image', params.file);
      
      const response = await apiClient.post(`/machines/${params.id}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload image');
    }
  }
);

const machinesSlice = createSlice({
  name: 'machines',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setFilters: (state, action: PayloadAction<{ location?: string; status?: string }>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchQuery = '';
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentMachine: (state) => {
      state.currentMachine = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch machines
    builder
      .addCase(fetchMachines.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMachines.fulfilled, (state, action) => {
        state.isLoading = false;
        state.machines = action.payload.data || [];
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.per_page || 20,
          total: action.payload.total_items || 0,
          totalPages: action.payload.total_pages || 0,
        };
      })
      .addCase(fetchMachines.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch single machine
    builder
      .addCase(fetchMachine.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMachine.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentMachine = action.payload;
      })
      .addCase(fetchMachine.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create machine
    builder
      .addCase(createMachine.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createMachine.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.machines.unshift(action.payload);
        }
      })
      .addCase(createMachine.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      });

    // Update machine
    builder
      .addCase(updateMachine.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateMachine.fulfilled, (state, action) => {
        state.isUpdating = false;
        const updatedMachine = action.payload;
        if (!updatedMachine || !updatedMachine.id) {
          return;
        }
        const index = state.machines.findIndex(m => m.id === updatedMachine.id);
        if (index !== -1) {
          state.machines[index] = updatedMachine;
        }
        if (state.currentMachine?.id === updatedMachine.id) {
          state.currentMachine = updatedMachine;
        }
      })
      .addCase(updateMachine.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });

    // Delete machine
    builder
      .addCase(deleteMachine.fulfilled, (state, action) => {
        state.machines = state.machines.filter(m => m.id !== action.payload);
        if (state.currentMachine?.id === action.payload) {
          state.currentMachine = null;
        }
      });

    // Upload image
    builder
      .addCase(uploadMachineImage.fulfilled, (state, action) => {
        if (state.currentMachine && action.payload.machine) {
          state.currentMachine = { ...state.currentMachine, ...action.payload.machine };
        }
      });
  },
});

export const {
  setSearchQuery,
  setFilters,
  clearFilters,
  clearError,
  clearCurrentMachine,
} = machinesSlice.actions;

export default machinesSlice.reducer;
